import { Fragment, useMemo, useState } from "react";
import { ActionList } from "../../components/ui";
import SortIcon from "../../components/ui/Sorting";
import SortSummary from "../../components/ui/SortSummary";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import type { CategorySummary } from "../../services/category/category.types";

const COLUMNS = [
    { key: "name", label: "Category Name", className: "category-name-col" },
    { key: "description", label: "Description", className: "category-description-col" },
    { key: "customers", label: "Customers", className: "category-customers-col" },
    { key: "sites", label: "Sites", className: "category-sites-col" },
] as const;
type SortColumn = typeof COLUMNS[number]["key"];

interface Props {
    categories: CategorySummary[];
    loading: boolean;
    canManage: boolean;
    busy: boolean;
    onEdit: (category: CategorySummary) => void;
    onDelete: (category: CategorySummary) => void;
}

export function CategoryList({ categories, loading, canManage, busy, onEdit, onDelete }: Props) {
    const [sortColumn, setSortColumn] = useState<SortColumn>("name");
    const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
    const [hasAppliedSort, setHasAppliedSort] = useState(false);
    const activeColumn = COLUMNS.find(column => column.key === sortColumn);
    const sorted = useMemo(() => {
        const value = (category: CategorySummary) => {
            switch (sortColumn) {
                case "name": return (category.name || "").toLowerCase();
                case "description": return category.description ?? "";
                case "customers": return category.associatedCustomers?.map(customer => customer.name).join(", ") ?? "";
                case "sites": return category.associatedSites?.map(site => site.siteName).join(", ") ?? "";
            }
        };
        return [...categories].sort((left, right) => {
            const first = value(left);
            const second = value(right);
            const comparison = sortColumn === "name"
                ? (first < second ? -1 : first > second ? 1 : 0)
                : first.localeCompare(second);
            return comparison * (sortOrder === "asc" ? 1 : -1);
        });
    }, [categories, sortColumn, sortOrder]);
    const sort = (column: SortColumn) => {
        setHasAppliedSort(true);
        setSortOrder(previous => sortColumn === column && previous === "asc" ? "desc" : "asc");
        setSortColumn(column);
    };
    const reset = () => { setSortColumn("name"); setSortOrder("asc"); setHasAppliedSort(false); };
    return (
        <>
            <div className="alarm-content--heading">
                <div>
                    <h3 className="heading heading__title--category">Category List</h3>
                    {hasAppliedSort && activeColumn && <SortSummary columnLabel={activeColumn.label} sortOrder={sortOrder}
                        onReset={reset} resetAriaLabel="Reset sorting to category name ascending" />}
                </div>
            </div>
            <div className="table-responsive">
                {loading ? <LoadingSpinner message="Loading categories…" /> : (
                    <table className="table categories-table">
                        <thead><tr>
                            {COLUMNS.map(column => <th key={column.key} className={`th--sortable ${column.className}`}
                                onClick={() => sort(column.key)}
                                aria-sort={sortColumn === column.key ? (sortOrder === "asc" ? "ascending" : "descending") : "none"}>
                                {column.label} <SortIcon sortOrder={sortColumn === column.key ? sortOrder : null} />
                            </th>)}
                            {canManage && <th className="actions-column">Actions</th>}
                        </tr></thead>
                        <tbody>
                            {!sorted.length && <tr><td colSpan={canManage ? 5 : 4} style={{ textAlign: "center" }}>No categories have been created yet.</td></tr>}
                            {sorted.map(category => <tr key={category.id}>
                                <td className="category-name-col">{category.name}</td>
                                <td className="categories-table__description">{category.description || "—"}</td>
                                <td className="categories-table__customers">
                                    {category.associatedCustomers?.length ? category.associatedCustomers.map((customer, index) => (
                                        <Fragment key={customer.id}>
                                            {index > 0 && ", "}
                                            <a className="alarm-name-link customer-name-cell__name" href={`/customers/${customer.id}`}>{customer.name || customer.id}</a>
                                        </Fragment>
                                    )) : category.associatedCustomers === undefined && category.associatedSites?.length ? "Unavailable" : "—"}
                                </td>
                                <td className="categories-table__sites">
                                    {category.associatedSites?.length ? category.associatedSites.map((site, index) => (
                                        <Fragment key={site.siteIdentifierRegistryId}>
                                            {index > 0 && ", "}
                                            <a className="alarm-name-link site-name-cell__name" href={`/sites/${site.siteIdentifierRegistryId}`}>{site.siteName || site.siteIdentifier}</a>
                                        </Fragment>
                                    )) : "—"}
                                </td>
                                {canManage && <td className="actions-column"><ActionList disabled={busy}
                                    onEdit={() => onEdit(category)} onDelete={() => onDelete(category)} /></td>}
                            </tr>)}
                        </tbody>
                    </table>
                )}
            </div>
        </>
    );
}
